/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Sebastian Hartmann <s.hartmann@techdivision.com>
 */

import { useEffect, useState } from 'react';
import { useBestFitQuery } from '../Hooks/Api/useBestFitQuery';
import { studioTagConfig } from '../Config/StudioTagConfig';
import { useSitePackageProvider } from '../Provider/SitePackageProvider';

/**
 * Custom useStudioLocatorMap Hook
 */
export const useStudioLocatorMap = () => {
  const { context, sitePackage } = useSitePackageProvider();
  const { data, isLoading } = useBestFitQuery(
    'studios',
    context === 'Development' ? {
      studioTags: `${studioTagConfig[sitePackage]},${studioTagConfig.SandboxStudio}`,
    } : {
      studioTags: studioTagConfig[sitePackage],
    },
  );

  const [studios, setStudios] = useState([]);
  const [currentSelectedMarker, setCurrentSelectedMarker] = useState(null);
  const [mobileDetailsVisible, setMobileDetailsVisible] = useState(false);

  useEffect(() => {
    if (data) {
      // Exclude Headquarter from studios - AIFITNESS-182
      const excludedStudios = ['1878668730'];
      setStudios(data.filter((studio) => !excludedStudios.includes((studio.id).toString())));
    }
  }, [data]);

  /**
     * Set selected marker to state
     * @param marker
     */
  const setSelectedMarker = (marker) => {
    setCurrentSelectedMarker(marker);
    setMobileDetailsVisible(true);
  };

  /**
     * Toggle the studio details on mobile
     */
  const toggleMobileStudioDetails = () => {
    setMobileDetailsVisible((value) => !value);
  };

  /**
     * Calculate distance between map center and the selected studio
     * @param mapsRef
     * @param searchMarkerPosition
     */
  const calculateDistanceBetween = (mapsRef, searchMarkerPosition) => {
    if (searchMarkerPosition) {
      const distanceBetween = mapsRef.geometry.spherical.computeDistanceBetween(new mapsRef.LatLng(
        searchMarkerPosition.lat(),
        searchMarkerPosition.lng(),
      ), new mapsRef.LatLng(
        currentSelectedMarker.address.latitude,
        currentSelectedMarker.address.longitude,
      ));
      const distanceInKm = distanceBetween / 1000;
      return Math.round(distanceInKm * 10) / 10;
    }
    return null;
  };

  return {
    isLoading,
    studios,
    currentSelectedMarker,
    setCurrentSelectedMarker,
    setSelectedMarker,
    calculateDistanceBetween,
    toggleMobileStudioDetails,
    mobileDetailsVisible,
  };
};
